// Paid registration (design/027) — shown to anyone signed in whose login has
// no account yet (/me.provisioned === false, paywall on). The account is
// created only when Stripe confirms the 7-day trial; until then this is the
// whole app. Steps:
//   1. (Apple "Hide My Email" logins) "Already have an account? Link it."
//   2. Start the free trial → Stripe Checkout (card up front).
//   3. Back from Stripe → "Setting up your account…" until /me is provisioned.
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithEmailAndPassword, signOut as fbSignOut } from 'firebase/auth';
import { useAuth } from '../auth/AuthContext';
import { linkingAuth } from '../lib/firebase';
import api from '../lib/api';
import PlanPicker, { defaultPlan, planTerms, priceLine } from '../components/PlanPicker';
const errText = (e) => e?.data?.message || e?.message || 'Something went wrong';

export default function RegistrationPage() {
  const { me, refreshMe, signOut } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // Only the subscription checkout returns to /billing. A ?checkout=… on any
  // other page (a course / practice / group / event purchase) isn't this
  // sign-up's trial, so don't wait for an account it won't create.
  const { pathname } = useLocation();
  const checkout = pathname.startsWith('/billing') ? params.get('checkout') : null;
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(checkout === 'success');
  const [timedOut, setTimedOut] = useState(false);
  const [skipLink, setSkipLink] = useState(false);
  const pollRef = useRef(null);

  const sub = me?.subscription || {};
  const trial = !!sub.trialEligible;
  const yearlyOn = !!sub.yearlyAvailable;
  const [plan, setPlan] = useState(defaultPlan(sub));
  useEffect(() => { setPlan(yearlyOn ? 'yearly' : 'monthly'); }, [yearlyOn]);

  // Back from Stripe: confirm with Stripe right away (creates the account
  // without waiting for the webhook), then poll /me as a backup.
  const sessionId = params.get('session_id');
  useEffect(() => {
    if (checkout !== 'success' || !sessionId) return;
    api.confirmCheckout(sessionId).then(async () => {
      const fresh = await refreshMe();
      if (fresh?.provisioned) navigate('/', { replace: true });
    }).catch(() => { /* polling below still runs */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkout, sessionId]);

  useEffect(() => {
    if (checkout !== 'success') return undefined;
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      const fresh = await refreshMe();
      if (fresh?.provisioned) {
        clearInterval(pollRef.current);
        navigate('/', { replace: true });
      } else if (tries >= 30) {
        clearInterval(pollRef.current);
        setWaiting(false);
        setTimedOut(true);
      }
    }, 2000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkout]);

  const startTrial = async () => {
    setBusy(true);
    try {
      const { url } = await api.startSubscriptionCheckout(plan);
      if (url) window.location.assign(url);
      else setBusy(false);
    } catch (e) { setBusy(false); alert(errText(e)); }
  };

  const dismissCancel = () => {
    const p = new URLSearchParams(params); p.delete('checkout');
    setParams(p, { replace: true });
  };

  const cancelSignup = async () => {
    if (!window.confirm('Cancel sign-up? Your login will be deleted and nothing is charged.')) return;
    try { await api.deleteMe(); } catch { /* sign out anyway */ }
    await signOut();
  };

  return (
    <div style={{ minHeight: '100%', display: 'flex', justifyContent: 'center', padding: '40px 16px', background: 'var(--bg, #f6f7fb)' }}>
      <div style={{ width: '100%', maxWidth: 520 }}>
        <div style={{ fontWeight: 800, fontSize: '1.1rem', marginBottom: 18 }}>Compassion Practice</div>

        {waiting ? (
          <Card>
            <h2 style={{ marginTop: 0 }}>Setting up your account…</h2>
            <p style={{ color: 'var(--muted)' }}>Your free trial is confirmed. This usually takes a few seconds.</p>
          </Card>
        ) : timedOut ? (
          <Card>
            <h2 style={{ marginTop: 0 }}>Almost there</h2>
            <p style={{ color: 'var(--muted)' }}>
              Your payment went through but your account is taking longer than usual to set up. Refresh this page in a minute.
            </p>
            <button className="btn btn-primary" onClick={() => window.location.reload()}>Refresh</button>
          </Card>
        ) : me?.privateRelayEmail && !skipLink ? (
          <LinkAccount
            onLinked={async () => { await refreshMe(); navigate('/', { replace: true }); }}
            onNew={() => setSkipLink(true)} />
        ) : (
          <>
            {checkout === 'cancel' && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: 12, marginBottom: 12, color: '#991b1b', display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ flex: 1 }}>Checkout canceled — you haven't been charged.</span>
                <button className="btn btn-sm" onClick={dismissCancel}>OK</button>
              </div>
            )}
            <Card>
              <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: 4 }}>Last step to create your account</div>
              <h2 style={{ margin: '0 0 4px' }}>{trial ? 'Start your 7-day free trial' : 'Choose your plan'}</h2>
              <div style={{ color: 'var(--muted)', marginBottom: 14 }}>
                {trial ? `Then ${priceLine(plan)}. Cancel anytime.` : 'Cancel anytime.'}
              </div>
              {yearlyOn && <PlanPicker value={plan} onChange={setPlan} disabled={busy} />}
              <ul style={{ lineHeight: 1.9, paddingLeft: 20, marginTop: 0 }}>
                <li>All free courses and practices</li>
                <li>Join events and groups, and their conversations</li>
                <li>Your notes and reflections, synced across devices</li>
                <li>One subscription works on the web, iPhone and Android</li>
              </ul>
              <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '12px 16px', fontSize: '1rem' }}
                disabled={busy} onClick={startTrial}>
                {busy ? 'Redirecting to secure checkout…' : (trial ? 'Start my free trial' : `Subscribe — ${priceLine(plan)}`)}
              </button>
              <div style={{ marginTop: 10, fontSize: '0.78rem', color: 'var(--muted)', lineHeight: 1.5 }}>
                {planTerms(plan, trial)}
                {' '}Your account is created as soon as the trial starts. Payments are processed securely by Stripe.
              </div>
            </Card>
          </>
        )}

        <div style={{ marginTop: 14, fontSize: '0.8rem', color: 'var(--muted)', display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <span>Signed in as {me?.email || '…'}</span>
          <a onClick={() => signOut()} style={{ cursor: 'pointer' }}>Sign out</a>
          <a onClick={cancelSignup} style={{ cursor: 'pointer' }}>Cancel sign-up</a>
          <span style={{ flex: 1 }} />
          <a href="/terms" target="_blank" rel="noreferrer">Terms</a>
          <a href="/privacy" target="_blank" rel="noreferrer">Privacy</a>
        </div>
      </div>
    </div>
  );
}

function Card({ children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 14, padding: 22, boxShadow: 'var(--shadow-card)' }}>
      {children}
    </div>
  );
}

/**
 * Apple "Hide My Email" logins can't be matched to an existing account by
 * email. Signing in to the existing account (in a separate, in-memory
 * session) proves ownership; the server then attaches this login to it.
 */
function LinkAccount({ onLinked, onNew }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const link = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const a = await linkingAuth();
      const cred = await signInWithEmailAndPassword(a, email.trim(), password);
      const idToken = await cred.user.getIdToken();
      await api.linkAccount(idToken);
      await fbSignOut(a).catch(() => {});
      await onLinked();
    } catch (err) {
      const code = err?.code || '';
      setError(code.startsWith('auth/') ? 'That email and password didn’t match an account.' : errText(err));
      setBusy(false);
    }
  };

  return (
    <Card>
      <h2 style={{ marginTop: 0 }}>Already have an account?</h2>
      <p style={{ color: 'var(--muted)', marginTop: 0 }}>
        You signed in with Apple and chose to hide your email, so we can't tell whether you already have a
        Compassion Practice account. If you do, sign in to it once to link the two — you'll keep your
        subscription, courses and groups.
      </p>
      <form onSubmit={link}>
        <div className="form-field">
          <label>Email of your existing account</label>
          <input className="input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="form-field">
          <label>Password</label>
          <input className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {error && <div style={{ color: '#991b1b', fontSize: '0.875rem', marginBottom: 8 }}>{error}</div>}
        <button className="btn btn-primary" disabled={busy || !email.trim() || !password}>
          {busy ? 'Linking…' : 'Link my account'}
        </button>
      </form>
      <div style={{ marginTop: 14 }}>
        <a onClick={onNew} style={{ cursor: 'pointer' }}>No, I'm new here — continue</a>
      </div>
    </Card>
  );
}
